import { useState, MouseEvent, Fragment } from "react";
import { IndividualSmilesMoleculeHighlightingType } from "../../../routes/Resultsets/pages/IndividualResultset";
import ImageToggle from "../../Buttons/ImageToggle";
import MoleculeStructure from "../../Data/MoleculeStructure";
import { convertNumberToDecimal, downloadStructureImage } from "../../../utils/formattingFns";
import DomainBarPlot from "../HighlightGridComponents/DomainBarPlot";

import { ReactComponent as DownloadIcon } from '../../../assets/Icons/adornments/Download.svg';

const formatTable = (
    tableInfo: {
        featureImportanceType: string;
        featureImportanceMethod: string;
        colorDomain: string;
        absMax: number;
    }
) => {
    const { featureImportanceMethod, featureImportanceType, colorDomain } = tableInfo;

    return [
        {
            label: 'Feature Importance Type',
            value: featureImportanceType,
        },
        {
            label: 'Feature Importance Method',
            value: featureImportanceMethod
        },
        {
            label: 'Color Domain',
            value: colorDomain,
        }
    ];
}

export default function HighlightComponent({
    data,
    setPopupMessage
}: {
    data: {
        id: string,
        name: string,
        moleculeHighlightingData: IndividualSmilesMoleculeHighlightingType[],
        smiles: string,
        score: string | number | number[],
    },
    setPopupMessage: (popupMessage: string, isSuccessMessage: boolean) => void
}) {
    const [ selectedIndex, setSelectedIndex ] = useState(0);
    const [ normalizeColorDomain, setNormalizeColorDomain ] = useState(false);

    const normalizeAtomBondImportance = (
        atomBondImportance: { atoms: { [key: number]: number }, bonds: { [key: number]: number } },
        absMax: number
    ) => {
        if (!absMax || absMax <= 0) {
            return atomBondImportance;
        }

        return {
            atoms: Object.fromEntries(
                Object.entries(atomBondImportance.atoms).map(([key, value]) => [Number(key), Number(value) / absMax])
            ),
            bonds: Object.fromEntries(
                Object.entries(atomBondImportance.bonds).map(([key, value]) => [Number(key), Number(value) / absMax])
            ),
        };
    };

    const getTableInfo = (highlightingData: IndividualSmilesMoleculeHighlightingType) => {
        let tableData: {
            featureImportanceType: string,
            featureImportanceMethod: string,
            colorDomain: string,
            absMax: number,
        } = {
            featureImportanceType: '',
            featureImportanceMethod: '',
            colorDomain: '',
            absMax: 0,
        };

        const metadata = highlightingData.metadata;
        const jobMetadata = metadata['job_metadata'];
        if (jobMetadata) {
            const type = jobMetadata['feature_importance_type'];
            tableData.featureImportanceType = type ? type : '';
            const method = jobMetadata?.feature_importance_method ?? '';
            tableData.featureImportanceMethod = method ? method : '';
        }
        const atomBondImportance = highlightingData.dataframe.atom_bond_importance as { atoms: {[key: number]: number}, bonds: {[key: number]: number} };
        const atomBondImportanceValues = Object.values(atomBondImportance.atoms).concat(Object.values(atomBondImportance.bonds)).map(val => Number(val));

        const min = Number(convertNumberToDecimal(Math.min(...atomBondImportanceValues)));
        const max = Number(convertNumberToDecimal(Math.max(...atomBondImportanceValues)));

        if (min !== 0) {
            tableData.colorDomain = `[${min}, 0, ${max}]`
        } else {
            tableData.colorDomain = `[${min}, ${max}]`
        }

        tableData.absMax = Math.max(Math.abs(min), 0, Math.abs(max));

        return tableData;

    }

    const downloadStructure = (e: MouseEvent<HTMLButtonElement>, id: string) => {
        e.preventDefault();

        const structureId = `${id}-${selectedIndex}`;
        downloadStructureImage(e, data.smiles, structureId);
    }
    
    return (
        <div className="flex flex-col gap-6 p-6 justify-start items-stretch w-full">
            <div className={`w-full flex ${data.moleculeHighlightingData.length > 1 ? 'justify-between' : 'justify-end'} items-center`}>
                {data.moleculeHighlightingData.length > 1 && (
                    <ImageToggle 
                        selectedIndex={selectedIndex}
                        setSelectedIndex={setSelectedIndex}
                        totalIndices={data.moleculeHighlightingData.length}
                    />
                )}
                <div className='flex justify-end items-center gap-1'>
                    <button onClick={(e:any) => downloadStructure(e, data.id)} className="w-[24px] text-disable hover:text-primary transition-all">
                        <DownloadIcon />
                    </button>
                </div>
            </div>
            {data.moleculeHighlightingData.map((highlightingData, index) => {
                if (index === selectedIndex) {
                    const tableInfo = getTableInfo(highlightingData);
                    const atomBondImportance = highlightingData.dataframe.atom_bond_importance as {
                        atoms: { [key: number]: number },
                        bonds: { [key: number]: number },
                    };
                    const activeAtomBondImportance = normalizeColorDomain
                        ? normalizeAtomBondImportance(atomBondImportance, tableInfo.absMax)
                        : atomBondImportance;

                    const domainValues = Object.values(atomBondImportance.atoms)
                        .concat(Object.values(atomBondImportance.bonds))
                        .map(v => Number(v));
                    const rawMin = Math.min(...domainValues);
                    const rawMax = Math.max(...domainValues);
                    const normalizedMin = tableInfo.absMax > 0
                        ? Number(convertNumberToDecimal(rawMin / tableInfo.absMax))
                        : 0;
                    const normalizedMax = tableInfo.absMax > 0
                        ? Number(convertNumberToDecimal(rawMax / tableInfo.absMax))
                        : 0;
                    const activeColorDomain = normalizeColorDomain
                        ? (normalizedMin !== 0 ? `[${normalizedMin}, 0, ${normalizedMax}]` : `[${normalizedMin}, ${normalizedMax}]`)
                        : tableInfo.colorDomain;

                    const tableList = formatTable(tableInfo);
                    return (
                        <Fragment key={index}>
                            <div className="w-full flex justify-center items-center">
                                <MoleculeStructure 
                                    atom_bond_importance={activeAtomBondImportance} 
                                    smiles={data.smiles} 
                                    height={350} 
                                    width={350}
                                    id={`${data.id}-${selectedIndex}`}
                                />
                            </div>
                            <DomainBarPlot 
                                atom_bond_importance={activeAtomBondImportance} 
                                smiles={data.smiles} 
                                absMax={tableInfo.absMax}
                            />
                            <div className="flex flex-wrap justify-center items-stretch w-full">
                                {tableList.length > 0 && tableList.map(list => (
                                    <div className="flex flex-col justify-center items-stretch flex-1">
                                        <div className="p-2 border-b border-outline flex justify-center items-center">
                                            <p className="text-[12px] font-small text-quaternary text-center text-nowrap">{list.label}</p>
                                        </div>
                                        <div className="p-2 flex justify-center items-center">
                                            {list.label === 'Color Domain' ? (
                                                <div className="flex items-center gap-2">
                                                    <p className="text-[12px] text-default text-center text-nowrap">{activeColorDomain}</p>
                                                    <label className="flex items-center gap-1 text-[12px] text-default cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={normalizeColorDomain}
                                                            onChange={(e) => setNormalizeColorDomain(e.target.checked)}
                                                        />
                                                        Normalize
                                                    </label>
                                                </div>
                                            ) : (
                                                <p className="text-[12px] text-default text-center text-nowrap">{list.value}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Fragment>
                    )
                } else {
                    return null
                }
            })}
        </div>
    )
}